import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Download, FileText, Loader2, ShieldAlert, X } from 'lucide-react';
import { binder, type BulkExportResponse } from '@/api/client';
import { errorText } from '@/lib/permissionError';

/**
 * Sprint-18 (eISF spec §audit events — Bulk Export) — caller-facing UI.
 *
 * Opens a manifest preview + per-row download links. The server hands
 * back time-bounded presigned URLs (30 min); the dialog lets the user
 * pick documents to download individually or copy the manifest as CSV
 * for an offline batch fetch.
 */

interface BulkExportDialogProps {
  binderRef: string;
  open: boolean;
  onClose: () => void;
}

export function BulkExportDialog({ binderRef, open, onClose }: BulkExportDialogProps) {
  const [includeHistory, setIncludeHistory] = useState(false);
  const [data, setData] = useState<BulkExportResponse | null>(null);

  const exportQ = useMutation({
    mutationFn: () => binder.bulkExport(binderRef, { include_history: includeHistory }),
    onSuccess: (r) => {
      setData(r);
      toast.success(`Manifest ready — ${r.total_versions} version(s)`);
    },
    onError: (err) => toast.error(errorText(err, 'Export failed')),
  });

  if (!open) return null;

  function downloadManifestCsv() {
    if (!data) return;
    const header = [
      'document_reference_number', 'document_title', 'document_type', 'folder',
      'version', 'status', 'mime_type', 'file_name', 'file_size_bytes',
      'uploaded_at', 'checksum_sha256', 'phi_present', 'download_url',
    ].join(',');
    const rows = data.rows.map((r) =>
      [
        csv(r.document_reference_number ?? ''),
        csv(r.document_title),
        csv(r.document_type_name),
        csv(r.folder_breadcrumb),
        csv(r.version_label),
        csv(r.status),
        csv(r.mime_type),
        csv(r.file_name),
        String(r.file_size),
        csv(r.uploaded_at),
        csv(r.checksum_sha256 ?? ''),
        String(r.phi_present),
        csv(r.download_url),
      ].join(','),
    );
    const csvText = [header, ...rows].join('\n');
    const blob = new Blob([csvText], { type: 'text/csv;charset=utf-8;' });
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = `${binderRef}-manifest-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(objectUrl);
    toast.success('Manifest CSV downloaded');
  }

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-ink-primary/40 p-4" onClick={onClose}>
      <div
        className="bg-white rounded-md shadow-2xl w-full max-w-4xl max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="px-5 py-4 border-b border-surface-border flex items-center gap-3">
          <Download className="h-5 w-5 text-accent-600" />
          <div>
            <div className="text-base font-semibold text-ink-primary">Bulk export binder</div>
            <div className="text-xs text-ink-muted">
              Generates time-bounded download URLs for inspection / monitor handoff.
              Audited as `access.bulk.export`.
            </div>
          </div>
          <button type="button" onClick={onClose} className="ml-auto text-ink-muted hover:text-ink-primary">
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="px-5 py-3 border-b border-surface-border flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={includeHistory}
              onChange={(e) => setIncludeHistory(e.target.checked)}
            />
            Include full version history (not just current)
          </label>
          <button
            type="button"
            onClick={() => exportQ.mutate()}
            disabled={exportQ.isPending}
            className="btn-primary ml-auto h-9 text-sm"
          >
            {exportQ.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
            {data ? 'Regenerate manifest' : 'Generate manifest'}
          </button>
          {data && (
            <button
              type="button"
              onClick={downloadManifestCsv}
              className="inline-flex items-center gap-1.5 rounded border border-surface-border bg-white px-3.5 h-9 text-sm font-medium text-ink-primary hover:bg-surface-muted/50"
            >
              <FileText className="h-3.5 w-3.5" />
              Download CSV
            </button>
          )}
        </div>

        <div className="flex-1 overflow-y-auto">
          {!data ? (
            <div className="p-10 text-center text-sm text-ink-muted">
              Generate the manifest to see the documents available for download.
            </div>
          ) : data.rows.length === 0 ? (
            <div className="p-10 text-center text-sm text-ink-muted">
              <FileText className="mx-auto mb-2 h-8 w-8 opacity-40" />
              {data.include_history
                ? 'No documents have been uploaded to this binder yet.'
                : (
                  <>
                    No approved documents yet — only fully-approved (current) versions are
                    included by default.
                    <br />
                    Check "Include full version history" above to also include drafts and
                    documents pending approval.
                  </>
                )}
            </div>
          ) : (
            <>
              <div className="px-5 py-2.5 bg-surface-muted/30 text-xs text-ink-muted flex items-center gap-4 border-b border-surface-border">
                <span><b>{data.total_versions}</b> versions</span>
                {data.phi_count > 0 && (
                  <span className="inline-flex items-center gap-1 text-amber-800">
                    <ShieldAlert className="h-3 w-3" /> {data.phi_count} PHI-flagged
                  </span>
                )}
                <span className="ml-auto">URLs expire {new Date(data.expires_at).toLocaleString()}</span>
              </div>
              <table className="w-full text-sm">
                <thead className="bg-surface-muted/40 text-left text-[11px] uppercase tracking-wider text-ink-muted">
                  <tr>
                    <th className="px-4 py-2">Document</th>
                    <th className="px-4 py-2">Folder</th>
                    <th className="px-4 py-2">Version</th>
                    <th className="px-4 py-2">Size</th>
                    <th className="px-4 py-2 text-right">Download</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((r) => (
                    <tr key={r.version_id} className="border-b border-surface-border/30">
                      <td className="px-4 py-2">
                        <div className="font-medium text-ink-primary truncate max-w-md">{r.document_title}</div>
                        <div className="font-mono text-[10px] text-ink-muted">{r.document_reference_number ?? '—'}</div>
                        {r.phi_present && (
                          <span className="inline-flex items-center gap-0.5 mt-0.5 text-[10px] text-amber-800">
                            <ShieldAlert className="h-2.5 w-2.5" /> PHI
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2 text-xs text-ink-muted truncate max-w-xs">{r.folder_breadcrumb || '—'}</td>
                      <td className="px-4 py-2 font-mono text-xs">{r.version_label}</td>
                      <td className="px-4 py-2 text-xs">{(r.file_size / 1024).toFixed(0)} KB</td>
                      <td className="px-4 py-2 text-right">
                        <a
                          href={r.download_url}
                          download={r.file_name}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 rounded border border-surface-border bg-white px-2.5 py-1 text-xs font-medium text-ink-primary hover:bg-surface-muted/50"
                        >
                          <Download className="h-3 w-3" />
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function csv(s: string): string {
  if (s.includes(',') || s.includes('"') || s.includes('\n')) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}
